import { useState, useRef } from 'react'
import { X, User, BarChart3, ImageIcon, Handshake, Mail, Download, Upload, Plus, Trash2, Palette, Database, Link } from 'lucide-react'
import { platformNames, platformColors, platformIcons } from '../data/config'

function EditPanel({ isOpen, onClose, config, actions }) {
  const [activeTab, setActiveTab] = useState('profile')
  const [isExporting, setIsExporting] = useState(false)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [avatarMessage, setAvatarMessage] = useState(null)
  const fileInputRef = useRef(null)
  const avatarMsgTimer = useRef(null)

  // 格式化数字（转换为 w/k 格式）
  const formatNumber = (num) => {
    if (typeof num === 'string') {
      // 如果已经是字符串格式（如 "1.2w"），直接返回
      if (num.includes('w') || num.includes('k') || num.includes('万')) return num
      num = parseInt(num.replace(/,/g, '')) || 0
    }
    if (num >= 10000) {
      return (num / 10000).toFixed(1) + 'w'
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k'
    }
    return num.toString()
  }

  // 根据URL自动识别平台
  const detectPlatformFromUrl = (url) => {
    if (!url) return null
    try {
      const hostname = url.toLowerCase()
      if (hostname.includes('douyin.com') || hostname.includes('iesdouyin.com')) return 'douyin'
      if (hostname.includes('xiaohongshu.com') || hostname.includes('xhslink.com')) return 'xiaohongshu'
      if (hostname.includes('bilibili.com') || hostname.includes('b23.tv')) return 'bilibili'
      if (hostname.includes('kuaishou.com') || hostname.includes('chenzhongtech.com')) return 'kuaishou'
      if (hostname.includes('ixigua.com') || hostname.includes('xigua.com')) return 'xigua'
    } catch (e) {
      // URL解析失败，静默忽略
    }
    return null
  }

  if (!isOpen) return null

  const tabs = [
    { id: 'profile', label: '个人资料', icon: User },
    { id: 'platforms', label: '平台数据', icon: BarChart3 },
    { id: 'works', label: '作品管理', icon: ImageIcon },
    { id: 'collaboration', label: '合作信息', icon: Handshake },
    { id: 'contact', label: '联系方式', icon: Mail },
    { id: 'appearance', label: '外观设置', icon: Palette },
    { id: 'import', label: '数据导入', icon: Database },
  ]

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return

    // 清除旧消息
    if (avatarMsgTimer.current) {
      clearTimeout(avatarMsgTimer.current)
    }

    // 文件大小检查（限制5MB）
    const maxSize = 5 * 1024 * 1024
    if (file.size > maxSize) {
      setAvatarMessage({ type: 'error', text: '图片过大，请选择小于5MB的图片' })
      avatarMsgTimer.current = setTimeout(() => setAvatarMessage(null), 4000)
      e.target.value = ''
      return
    }

    // 文件类型检查
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      setAvatarMessage({ type: 'error', text: '不支持的文件格式，请选择JPG/PNG/GIF/WebP图片' })
      avatarMsgTimer.current = setTimeout(() => setAvatarMessage(null), 4000)
      e.target.value = ''
      return
    }

    setAvatarUploading(true)
    setAvatarMessage({ type: 'info', text: '正在处理头像...' })

    try {
      const reader = new FileReader()
      reader.onload = (event) => {
        try {
          const img = new Image()
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas')
              const size = 400
              canvas.width = size
              canvas.height = size
              const ctx = canvas.getContext('2d')
              // 居中裁剪为正方形
              const minDim = Math.min(img.width, img.height)
              const sx = (img.width - minDim) / 2
              const sy = (img.height - minDim) / 2
              ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, size, size)
              const compressed = canvas.toDataURL('image/jpeg', 0.8)
              actions.updateProfile({ avatar: compressed })
              setAvatarUploading(false)
              setAvatarMessage({ type: 'success', text: '头像上传成功！' })
              avatarMsgTimer.current = setTimeout(() => setAvatarMessage(null), 3000)
            } catch (err) {
              setAvatarUploading(false)
              setAvatarMessage({ type: 'error', text: '图片处理失败：' + err.message })
              avatarMsgTimer.current = setTimeout(() => setAvatarMessage(null), 4000)
            }
          }
          img.onerror = () => {
            setAvatarUploading(false)
            setAvatarMessage({ type: 'error', text: '图片加载失败，请重试' })
            avatarMsgTimer.current = setTimeout(() => setAvatarMessage(null), 4000)
          }
          img.src = event.target.result
        } catch (err) {
          setAvatarUploading(false)
          setAvatarMessage({ type: 'error', text: '图片读取失败：' + err.message })
          avatarMsgTimer.current = setTimeout(() => setAvatarMessage(null), 4000)
        }
      }
      reader.onerror = () => {
        setAvatarUploading(false)
        setAvatarMessage({ type: 'error', text: '文件读取失败，请重试' })
        avatarMsgTimer.current = setTimeout(() => setAvatarMessage(null), 4000)
      }
      reader.readAsDataURL(file)
    } catch (err) {
      setAvatarUploading(false)
      setAvatarMessage({ type: 'error', text: '上传失败：' + err.message })
      avatarMsgTimer.current = setTimeout(() => setAvatarMessage(null), 4000)
    } finally {
      // 重置input以允许重新选择同一个文件
      e.target.value = ''
    }
  }

  const handleWorkCoverUpload = (e, workId) => {
    const file = e.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const maxWidth = 800
        const scale = Math.min(1, maxWidth / img.width)
        canvas.width = img.width * scale
        canvas.height = img.height * scale
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        const compressed = canvas.toDataURL('image/jpeg', 0.8)

        const updatedWorks = config.works.map(w =>
          w.id === workId ? { ...w, cover: compressed } : w
        )
        actions.updateWorks(updatedWorks)
      }
      img.src = event.target.result
    }
    reader.readAsDataURL(file)
  }

  const handleExport = () => {
    setIsExporting(true)
    actions.exportConfig()
    setTimeout(() => setIsExporting(false), 1000)
  }

  const handleImport = (e) => {
    const file = e.target.files[0]
    if (file) {
      actions.importConfig(file).then(() => {
        alert('配置导入成功！')
      }).catch(err => {
        alert('导入失败：' + err.message)
      })
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex">
      <style>{`
        .scrollbar-thin::-webkit-scrollbar {
          height: 4px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: transparent;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: #374151;
          border-radius: 2px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: #4b5563;
        }
      `}</style>
      {/* 遮罩 */}
      <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* 面板 */}
      <div className="w-full max-w-2xl bg-gray-900 border-l border-gray-800 flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-800">
          <h2 className="text-xl font-semibold text-white">编辑网站内容</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 标签栏 */}
        <div className="flex border-b border-gray-800 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 sm:px-5 py-3 sm:py-4 text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
                activeTab === tab.id
                  ? 'text-accent border-b-2 border-accent bg-accent/10'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <tab.icon className="w-4 h-4 flex-shrink-0" />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.label.slice(0, 2)}</span>
            </button>
          ))}
        </div>

        {/* 内容区 */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* 个人资料 */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">头像</label>
                <div className="flex items-center gap-4">
                  <div className="relative w-20 h-20 rounded-full bg-gray-800 flex items-center justify-center overflow-hidden">
                    {config.profile.avatar ? (
                      <img src={config.profile.avatar} alt="头像" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-8 h-8 text-gray-500" />
                    )}
                    {avatarUploading && (
                      <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className={`px-4 py-2 ${avatarUploading ? 'bg-accent/50 cursor-not-allowed' : 'bg-accent hover:bg-accent cursor-pointer'} text-white text-sm rounded-lg transition-colors`}>
                      {avatarUploading ? '处理中...' : '上传头像'}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/webp"
                        onChange={handleAvatarUpload}
                        className="hidden"
                        disabled={avatarUploading}
                      />
                    </label>
                    {avatarMessage && (
                      <span className={`text-xs ${
                        avatarMessage.type === 'error' ? 'text-red-400' :
                        avatarMessage.type === 'success' ? 'text-green-400' :
                        'text-accent'
                      }`}>
                        {avatarMessage.text}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">名称</label>
                <input
                  type="text"
                  value={config.profile.name}
                  onChange={(e) => actions.updateProfile({ name: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-accent focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">标语</label>
                <input
                  type="text"
                  value={config.profile.tagline}
                  onChange={(e) => actions.updateProfile({ tagline: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-accent focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">简介</label>
                <textarea
                  value={config.profile.bio}
                  onChange={(e) => actions.updateProfile({ bio: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-accent focus:outline-none resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">位置</label>
                <input
                  type="text"
                  value={config.profile.location}
                  onChange={(e) => actions.updateProfile({ location: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-accent focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">创作领域（逗号分隔）</label>
                <input
                  type="text"
                  value={config.profile.niche?.join(', ')}
                  onChange={(e) => actions.updateProfile({ niche: e.target.value.split(',').map(s => s.trim()) })}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-accent focus:outline-none"
                  placeholder="生活方式, 美食探店, 旅行攻略"
                />
              </div>
            </div>
          )}

          {/* 平台数据 */}
          {activeTab === 'platforms' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">总粉丝数</label>
                  <input
                    type="text"
                    value={config.stats.totalFollowers}
                    onChange={(e) => actions.updateStats({ totalFollowers: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-accent focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">总作品数</label>
                  <input
                    type="number"
                    value={config.stats.totalWorks}
                    onChange={(e) => actions.updateStats({ totalWorks: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-accent focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">总获赞数</label>
                  <input
                    type="text"
                    value={config.stats.totalLikes}
                    onChange={(e) => actions.updateStats({ totalLikes: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-accent focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">品牌合作数</label>
                  <input
                    type="number"
                    value={config.stats.brandCollabs}
                    onChange={(e) => actions.updateStats({ brandCollabs: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-accent focus:outline-none"
                  />
                </div>
              </div>

              <div className="border-t border-gray-800 pt-6">
                <h3 className="text-lg font-medium text-white mb-4">各平台数据</h3>
                {Object.entries(config.platforms).map(([key, platform]) => (
                  <div key={key} className="mb-6 p-4 bg-gray-800/50 rounded-lg">
                    <h4 className="font-medium text-white mb-3" style={{ color: platformColors[key] }}>
                      {platform.icon} {platform.name}
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">用户名</label>
                        <input
                          type="text"
                          placeholder="@创作者小助手"
                          value={platform.username}
                          onChange={(e) => actions.updatePlatform(key, { username: e.target.value })}
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-white focus:border-accent focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">粉丝数</label>
                        <input
                          type="text"
                          placeholder="128.5w"
                          value={platform.followers}
                          onChange={(e) => actions.updatePlatform(key, { followers: e.target.value })}
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-white focus:border-accent focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">作品数</label>
                        <input
                          type="number"
                          placeholder="328"
                          value={platform.works}
                          onChange={(e) => actions.updatePlatform(key, { works: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-white focus:border-accent focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">获赞数</label>
                        <input
                          type="text"
                          placeholder="2560w+"
                          value={platform.likes}
                          onChange={(e) => actions.updatePlatform(key, { likes: e.target.value })}
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-white focus:border-accent focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 作品管理 */}
          {activeTab === 'works' && (
            <div className="space-y-4">
              {config.works.map((work) => (
                <div key={work.id} className="p-4 bg-gray-800/50 rounded-lg">
                  <div className="flex items-start gap-4">
                    <div className="relative w-20 h-20 rounded-lg bg-gray-800 overflow-hidden flex-shrink-0">
                      {work.cover ? (
                        <img src={work.cover} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-6 h-6 text-gray-500" />
                        </div>
                      )}
                      <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 cursor-pointer transition-opacity">
                        <span className="text-xs text-white">更换</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleWorkCoverUpload(e, work.id)}
                          className="hidden"
                        />
                      </label>
                    </div>
                    <div className="flex-1 space-y-2">
                      {/* 封面链接输入 */}
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1.5 text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                          <ImageIcon className="w-3.5 h-3.5" />
                          封面链接
                        </label>
                        <input
                          type="url"
                          value={work.cover || ''}
                          onChange={(e) => {
                            const updated = config.works.map(w =>
                              w.id === work.id ? { ...w, cover: e.target.value } : w
                            )
                            actions.updateWorks(updated)
                          }}
                          className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white focus:border-accent focus:outline-none"
                          placeholder="粘贴封面图片链接"
                        />
                      </div>
                      <input
                        type="text"
                        value={work.title}
                        onChange={(e) => {
                          const updated = config.works.map(w =>
                            w.id === work.id ? { ...w, title: e.target.value } : w
                          )
                          actions.updateWorks(updated)
                        }}
                        className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white focus:border-accent focus:outline-none"
                        placeholder="作品标题"
                      />
                      <textarea
                        value={work.description}
                        onChange={(e) => {
                          const updated = config.works.map(w =>
                            w.id === work.id ? { ...w, description: e.target.value } : w
                          )
                          actions.updateWorks(updated)
                        }}
                        rows={2}
                        className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white focus:border-accent focus:outline-none resize-none"
                        placeholder="作品描述"
                      />
                      <div className="flex gap-2 items-center">
                        <select
                          value={work.platform}
                          onChange={(e) => {
                            const updated = config.works.map(w =>
                              w.id === work.id ? { ...w, platform: e.target.value } : w
                            )
                            actions.updateWorks(updated)
                          }}
                          className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white focus:border-accent focus:outline-none"
                        >
                          {Object.entries(platformNames).map(([key, name]) => (
                            <option key={key} value={key}>{name}</option>
                          ))}
                        </select>
                        <span className="text-xs text-gray-500 whitespace-nowrap">点赞</span>
                        <input
                          type="text"
                          value={work.likes}
                          onChange={(e) => {
                            const updated = config.works.map(w =>
                              w.id === work.id ? { ...w, likes: e.target.value } : w
                            )
                            actions.updateWorks(updated)
                          }}
                          className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white focus:border-accent focus:outline-none"
                          placeholder="点赞数"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1.5 text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                          <Link className="w-3.5 h-3.5" />
                          作品链接
                        </label>
                        <input
                          type="url"
                          value={work.url || ''}
                          onChange={(e) => {
                            const newUrl = e.target.value
                            const detectedPlatform = detectPlatformFromUrl(newUrl)
                            const updated = config.works.map(w => {
                              if (w.id === work.id) {
                                return {
                                  ...w,
                                  url: newUrl,
                                  // 如果检测到平台且当前平台为默认值，自动更新
                                  ...(detectedPlatform && { platform: detectedPlatform })
                                }
                              }
                              return w
                            })
                            actions.updateWorks(updated)
                          }}
                          className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white focus:border-accent focus:outline-none"
                          placeholder="粘贴作品原链接，自动识别平台"
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => actions.deleteWork(work.id)}
                      className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}

              <button
                onClick={() => {
                  actions.addWork({
                    title: '新作品',
                    platform: 'douyin',
                    cover: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=600&h=800&fit=crop',
                    description: '作品描述',
                    likes: '1w',
                    comments: '1k',
                    shares: '500',
                    views: '10w',
                    url: '#',
                    publishDate: new Date().toISOString().split('T')[0]
                  })
                }}
                className="w-full py-3 border-2 border-dashed border-gray-700 text-gray-400 rounded-lg hover:border-accent hover:text-accent transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                添加作品
              </button>
            </div>
          )}

          {/* 合作信息 */}
          {activeTab === 'collaboration' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">合作描述</label>
                <textarea
                  value={config.collaboration?.description || ''}
                  onChange={(e) => actions.updateCollaboration({ description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-accent focus:outline-none resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">合作类型（逗号分隔）</label>
                <input
                  type="text"
                  value={config.collaboration?.types?.join(', ')}
                  onChange={(e) => actions.updateCollaboration({ types: e.target.value.split(',').map(s => s.trim()) })}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-accent focus:outline-none"
                  placeholder="产品测评, 探店打卡, 品牌植入"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">已合作品牌（逗号分隔）</label>
                <input
                  type="text"
                  value={config.collaboration?.brands?.join(', ')}
                  onChange={(e) => actions.updateCollaboration({ brands: e.target.value.split(',').map(s => s.trim()) })}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-accent focus:outline-none"
                  placeholder="品牌A, 品牌B, 品牌C"
                />
              </div>
            </div>
          )}

          {/* 联系方式 */}
          {activeTab === 'contact' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">商务邮箱</label>
                <input
                  type="email"
                  value={config.contact.email}
                  onChange={(e) => actions.updateContact({ email: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-accent focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">联系电话</label>
                <input
                  type="text"
                  value={config.contact.phone}
                  onChange={(e) => actions.updateContact({ phone: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-accent focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">微信号</label>
                <input
                  type="text"
                  value={config.contact.wechat}
                  onChange={(e) => actions.updateContact({ wechat: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-accent focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">工作时间</label>
                <input
                  type="text"
                  value={config.contact.workHours}
                  onChange={(e) => actions.updateContact({ workHours: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-accent focus:outline-none"
                />
              </div>

              <div className="border-t border-gray-800 pt-6">
                <h3 className="text-lg font-medium text-white mb-4">社交链接</h3>
                {Object.entries(config.social).map(([key, url]) => (
                  <div key={key} className="mb-3">
                    <label className="block text-xs font-medium text-gray-400 mb-1 capitalize">
                      {platformNames[key] || key}
                    </label>
                    <input
                      type="text"
                      value={url}
                      onChange={(e) => actions.updateSocial({ [key]: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-white focus:border-accent focus:outline-none"
                      placeholder={`https://${key}.com/...`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 外观设置 */}
          {activeTab === 'appearance' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">背景类型</label>
                <div className="flex gap-3">
                  <button
                    onClick={() => actions.updateProfile({ backgroundType: 'color' })}
                    className={`flex-1 py-2 rounded-lg border ${config.profile?.backgroundType === 'color' || !config.profile?.backgroundType ? 'border-accent bg-accent/20 text-accent/70' : 'border-gray-700 text-gray-400'}`}
                  >
                    纯色背景
                  </button>
                  <button
                    onClick={() => actions.updateProfile({ backgroundType: 'image' })}
                    className={`flex-1 py-2 rounded-lg border ${config.profile?.backgroundType === 'image' ? 'border-accent bg-accent/20 text-accent/70' : 'border-gray-700 text-gray-400'}`}
                  >
                    图片背景
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">背景明亮度</label>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500">🌙暗</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={config.profile?.backgroundBrightness ?? 30}
                    onChange={(e) => actions.updateProfile({ backgroundBrightness: parseInt(e.target.value) })}
                    className="flex-1 h-2 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, var(--accent-dark, #4a2c6e), var(--accent, #8b5cf6), var(--accent-light, #c4b5fd))`,
                      accentColor: config.profile?.accentColor || '#8b5cf6'
                    }}
                  />
                  <span className="text-xs text-gray-500">☀️亮</span>
                  <span className="text-xs text-accent w-8 text-right">{config.profile?.backgroundBrightness ?? 30}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">全局字体</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'inter', label: 'Inter 现代', preview: '现代简约 · 默认' },
                    { key: 'noto-sans', label: '思源黑体', preview: '清晰易读 · 通用' },
                    { key: 'noto-serif', label: '思源宋体', preview: '典雅传统 · 文艺' },
                    { key: 'zcool', label: '站酷快乐体', preview: '活泼可爱 · 趣味' },
                    { key: 'xiaowei', label: '站酷小薇', preview: '清秀文艺 · 手账' },
                    { key: 'mashan', label: '马山正楷', preview: '书法韵味 · 古朴' },
                    { key: 'longcang', label: '龙藏体', preview: '潇洒飘逸 · 国风' },
                    { key: 'system', label: '系统默认', preview: '各平台原生字体' },
                  ].map((font) => {
                    const fontStyles = {
                      inter: { fontFamily: "'Inter', sans-serif" },
                      'noto-sans': { fontFamily: "'Noto Sans SC', sans-serif" },
                      'noto-serif': { fontFamily: "'Noto Serif SC', serif" },
                      zcool: { fontFamily: "'ZCOOL KuaiLe', cursive" },
                      xiaowei: { fontFamily: "'ZCOOL XiaoWei', serif" },
                      mashan: { fontFamily: "'Ma Shan Zheng', cursive" },
                      longcang: { fontFamily: "'Long Cang', cursive" },
                      system: { fontFamily: "system-ui, sans-serif" },
                    }
                    const isSelected = (config.profile?.fontFamily || 'inter') === font.key
                    return (
                      <button
                        key={font.key}
                        onClick={() => actions.updateProfile({ fontFamily: font.key })}
                        className={`text-left p-3 rounded-lg border transition-all ${
                          isSelected
                            ? 'border-accent bg-accent/10'
                            : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                        }`}
                      >
                        <div className={`text-sm font-medium ${isSelected ? 'text-accent/70' : 'text-gray-300'}`}>
                          {font.label}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5" style={fontStyles[font.key]}>
                          {font.preview}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">全局字体颜色</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={config.profile?.fontColor || '#ffffff'}
                    onChange={(e) => actions.updateProfile({ fontColor: e.target.value })}
                    className="w-12 h-10 rounded-lg cursor-pointer border-0 p-0"
                  />
                  <span className="text-xs text-gray-400">{config.profile?.fontColor || '#ffffff'}</span>
                  <div className="flex gap-1 ml-2">
                    {['#ffffff', '#000000', '#1a1a2e', '#f5f0eb', '#3b82f6', '#10b981', '#f59e0b'].map((c) => (
                      <button
                        key={c}
                        onClick={() => actions.updateProfile({ fontColor: c })}
                        className="w-6 h-6 rounded-full border-2 border-gray-600"
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">主题强调色</label>
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="color"
                    value={config.profile?.accentColor || '#8b5cf6'}
                    onChange={(e) => actions.updateProfile({ accentColor: e.target.value })}
                    className="w-12 h-10 rounded-lg cursor-pointer border-0 p-0"
                  />
                  <span className="text-xs text-gray-400">{config.profile?.accentColor || '#8b5cf6'}</span>
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {['#8b5cf6', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#f97316', '#6366f1', '#14b8a6'].map((c) => (
                    <button
                      key={c}
                      onClick={() => actions.updateProfile({ accentColor: c })}
                      className={`w-full aspect-square rounded-lg border-2 transition-all ${
                        (config.profile?.accentColor || '#8b5cf6') === c
                          ? 'border-white scale-110'
                          : 'border-transparent hover:border-gray-500'
                      }`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">板块背景色（下方所有板块）</label>
                <div className="grid grid-cols-5 gap-2">
                  {['#000000', '#0a0a0a', '#111111', '#1a1a2e', '#16213e', '#0f0f23', '#1a0a2e', '#0d1b2a', '#1b263b', '#212f45',
                    '#4a1942', '#2d1b69', '#1a3a2a', '#4a2c6e', '#8b3a62', '#1e3a5f', '#2d4a3e', '#5c2d2d', '#6b2d5b', '#1a2d4a',
                    '#f5f0eb', '#e8e0d5', '#d4c5b9', '#c9d6de', '#e8e2f0', '#f0ebe3', '#dce8f0', '#e6ded8'].map((color) => (
                    <button
                      key={color}
                      onClick={() => actions.updateProfile({ sectionBackground: color })}
                      className={`w-full aspect-square rounded-lg border-2 ${config.profile?.sectionBackground === color ? 'border-accent' : 'border-transparent'}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <input
                    type="color"
                    value={config.profile?.sectionBackground || '#000000'}
                    onChange={(e) => actions.updateProfile({ sectionBackground: e.target.value })}
                    className="w-12 h-10 rounded-lg cursor-pointer"
                  />
                  <span className="text-xs text-gray-400">自定义颜色</span>
                  <span className="text-xs text-accent ml-auto">{config.profile?.sectionBackground || '#000000'}</span>
                </div>
              </div>

              {(config.profile?.backgroundType === 'color' || !config.profile?.backgroundType) && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">背景颜色</label>
                  <div className="grid grid-cols-5 gap-2">
                    {['#1a1a2e', '#16213e', '#0f0f23', '#1a0a2e', '#0d1b2a', '#1b263b', '#212f45', '#4a1942', '#2d1b69', '#1a3a2a',
                      '#4a2c6e', '#8b3a62', '#1e3a5f', '#2d4a3e', '#5c2d2d', '#6b2d5b', '#1a2d4a', '#3d1f1f'].map((color) => (
                      <button
                        key={color}
                        onClick={() => actions.updateProfile({ backgroundColor: color })}
                        className={`w-full aspect-square rounded-lg border-2 ${config.profile?.backgroundColor === color ? 'border-accent' : 'border-transparent'}`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                  <input
                    type="color"
                    value={config.profile?.backgroundColor || '#000000'}
                    onChange={(e) => actions.updateProfile({ backgroundColor: e.target.value })}
                    className="mt-3 w-full h-10 rounded-lg cursor-pointer"
                  />
                </div>
              )}

              {config.profile?.backgroundType === 'image' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">背景图片</label>
                  <div className="space-y-3">
                    {config.profile?.backgroundImage && (
                      <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-800">
                        <img src={config.profile.backgroundImage} alt="背景" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <label className="flex items-center justify-center gap-2 w-full py-3 bg-gray-800 border border-gray-700 border-dashed rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
                      <Upload className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-400">上传背景图片</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0]
                          if (!file) return
                          const reader = new FileReader()
                          reader.onload = (event) => {
                            const img = new Image()
                            img.onload = () => {
                              const canvas = document.createElement('canvas')
                              const maxWidth = 1920
                              const scale = Math.min(1, maxWidth / img.width)
                              canvas.width = img.width * scale
                              canvas.height = img.height * scale
                              const ctx = canvas.getContext('2d')
                              ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
                              const compressed = canvas.toDataURL('image/jpeg', 0.8)
                              actions.updateProfile({ backgroundImage: compressed })
                            }
                            img.src = event.target.result
                          }
                          reader.readAsDataURL(file)
                        }}
                        className="hidden"
                      />
                    </label>
                    {config.profile?.backgroundImage && (
                      <button
                        onClick={() => actions.updateProfile({ backgroundImage: null })}
                        className="w-full py-2 text-red-400 text-sm hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        删除背景图片
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">背景效果</label>
                <select
                  value={config.profile?.backgroundEffect || 'particles'}
                  onChange={(e) => actions.updateProfile({ backgroundEffect: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-accent focus:outline-none"
                >
                  <option value="particles">粒子动画</option>
                  <option value="gradient">渐变流动</option>
                  <option value="none">无效果</option>
                </select>
              </div>
            </div>
          )}

          {/* 数据导入 */}
          {activeTab === 'import' && (
            <div className="space-y-6">
              <div className="bg-accent/20 border border-accent/30 rounded-xl p-4">
                <h3 className="text-accent font-semibold mb-2">📥 浏览器插件数据导入</h3>
                <p className="text-gray-400 text-sm">
                  从浏览器插件抓取的数据文件中导入平台数据和作品。支持抖音、小红书、B站、快手、西瓜视频等平台。
                </p>
              </div>

              {/* 导入文件 */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">选择插件导出的JSON文件</label>
                <label className="flex flex-col items-center justify-center gap-3 w-full py-8 bg-gray-800 border-2 border-dashed border-gray-700 rounded-xl cursor-pointer hover:border-accent hover:bg-gray-800/80 transition-all">
                  <Database className="w-10 h-10 text-gray-500" />
                  <div className="text-center">
                    <p className="text-gray-300 font-medium">点击选择文件</p>
                    <p className="text-gray-500 text-sm mt-1">支持格式: douyin_xxx.json, xiaohongshu_xxx.json</p>
                  </div>
                  <input
                    type="file"
                    accept=".json"
                    onChange={(e) => {
                      const file = e.target.files[0]
                      if (!file) return

                      const reader = new FileReader()
                      reader.onload = (event) => {
                        try {
                          const pluginData = JSON.parse(event.target.result)

                          // 验证数据格式
                          if (!pluginData.platform || !pluginData.stats) {
                            alert('文件格式不正确，请选择浏览器插件导出的数据文件')
                            return
                          }

                          // 确认导入
                          if (!confirm(`检测到 ${pluginData.platformName || pluginData.platform} 平台数据，是否导入？`)) {
                            return
                          }

                          // 更新平台数据
                          const platformKey = pluginData.platform
                          actions.updatePlatform(platformKey, {
                            name: pluginData.platformName || platformNames[platformKey],
                            username: pluginData.username || '',
                            userId: pluginData.userId || '',
                            followers: formatNumber(pluginData.stats?.followers || 0),
                            following: formatNumber(pluginData.stats?.following || 0),
                            likes: formatNumber(pluginData.stats?.likes || 0),
                            works: pluginData.stats?.works || 0,
                            url: window.location.href
                          })

                          // 如果有头像，更新到个人资料
                          if (pluginData.avatar && !config.profile?.avatar) {
                            actions.updateProfile({ avatar: pluginData.avatar })
                          }

                          // 导入作品
                          if (pluginData.works && pluginData.works.length > 0) {
                            const existingUrls = new Set(config.works.map(w => w.url))
                            const newWorks = pluginData.works
                              .filter(w => !existingUrls.has(w.url))
                              .map(w => ({
                                id: Date.now() + Math.random(),
                                title: w.title || '未命名作品',
                                platform: platformKey,
                                cover: w.cover || 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=600&h=800&fit=crop',
                                description: w.description || '',
                                likes: formatNumber(w.likes || 0),
                                comments: formatNumber(w.comments || 0),
                                shares: formatNumber(w.shares || 0),
                                views: formatNumber(w.views || 0),
                                url: w.url || '#',
                                publishDate: w.publishDate || new Date().toISOString().split('T')[0]
                              }))

                            if (newWorks.length > 0) {
                              newWorks.forEach(work => actions.addWork(work))
                              alert(`成功导入 ${newWorks.length} 个新作品！`)
                            } else {
                              alert('所有作品已存在，未导入新作品')
                            }
                          }

                          // 更新统计数据
                          const totalWorks = config.works.length + (pluginData.works?.length || 0)
                          actions.updateStats({ totalWorks })

                          alert('数据导入成功！')
                        } catch (err) {
                          alert('导入失败: ' + err.message)
                        }
                      }
                      reader.readAsText(file)
                    }}
                    className="hidden"
                  />
                </label>
              </div>

              {/* 使用说明 */}
              <div className="bg-gray-800/50 rounded-xl p-4">
                <h4 className="text-white font-medium mb-3">使用步骤</h4>
                <ol className="space-y-2 text-gray-400 text-sm list-decimal list-inside">
                  <li>安装浏览器插件并访问目标平台主页</li>
                  <li>点击插件图标，开始抓取数据</li>
                  <li>等待抓取完成，点击"导出JSON"下载文件</li>
                  <li>回到本页面，点击上方上传区域选择文件</li>
                  <li>系统自动解析并导入平台数据和作品</li>
                </ol>
              </div>

              {/* 已导入的平台 */}
              <div>
                <h4 className="text-white font-medium mb-3">已导入的平台数据</h4>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(config.platforms).map(([key, platform]) => (
                    <div
                      key={key}
                      className={`p-3 rounded-lg border ${
                        platform.username && platform.username !== '@创作者小助手'
                          ? 'bg-accent/10 border-accent/30'
                          : 'bg-gray-800 border-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{platformIcons[key]}</span>
                        <span className="text-white font-medium">{platform.name}</span>
                      </div>
                      <div className="text-xs text-gray-400">
                        {platform.username && platform.username !== '@创作者小助手' ? (
                          <span className="text-accent">✓ 已导入</span>
                        ) : (
                          <span>未导入</span>
                        )}
                      </div>
                      {platform.followers && platform.followers !== '0' && (
                        <div className="text-sm text-gray-300 mt-1">
                          粉丝: {platform.followers}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* 批量导入提示 */}
              <div className="text-center p-4 bg-gray-800/30 rounded-xl">
                <p className="text-gray-400 text-sm">
                  提示: 可以多次导入不同平台的数据，系统会自动合并
                </p>
              </div>
            </div>
          )}
        </div>

        {/* 底部操作 */}
        <div className="p-6 border-t border-gray-800 space-y-3">
          <div className="flex gap-3">
            <button
              onClick={handleExport}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              {isExporting ? '已导出' : '导出配置'}
            </button>
            <label className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors cursor-pointer">
              <Upload className="w-4 h-4" />
              导入配置
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleImport}
                className="hidden"
              />
            </label>
          </div>
          <button
            onClick={actions.resetToDefault}
            className="w-full px-4 py-2 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/10 transition-colors text-sm"
          >
            重置为默认配置
          </button>
        </div>
      </div>
    </div>
  )
}

export default EditPanel
